import { ChangeDetectorRef, Component } from '@angular/core';
import { LanguageService } from '../../services/language.service';
import { PolicyLawsService } from './policy-laws.service';
import { PolicyLawsRequest } from './policy-laws.model';

@Component({
  selector: 'app-policy-laws',
  standalone: false,
  templateUrl: './policy-laws.component.html',
  styleUrls: ['./policy-laws.component.css']
})
export class PolicyLawsComponent {

  successMessage = '';
  errorMessage = '';

  languages: any[] = [];

  policyLaws: any[] = [];
  filteredPolicyLaws: any[] = [];

  currentPage = 1;
  pageSize = 5;

  activeTab = 'all';
  activeTabPopup = 1;

  isModalOpen = false;

  isEnableSave = false;
  isEnableUpdate = false;
  isEnableDelete = false;

  policyLawId = 0;
  order = 0;
  isActive = true;
  image: File | null = null;
  imagePreview = '';

  titleKh = '';
  summaryKh = '';
  titleEn = '';
  summaryEn = '';

  constructor(
    private policyLawsService: PolicyLawsService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadLanguages();
    this.loadPolicyLaws('');
  }

  loadLanguages() {
    this.languageService.getAll().subscribe((res: any) => {
      this.languages = res.data.sort((a: any, b: any) => a.languageId - b.languageId);

      if (this.languages.length > 0) {
        this.setTab(this.languages[0].code);
        this.setTabPopup(this.languages[0].languageId);
      }
    });
  }

  loadPolicyLaws(langCode: string) {
    this.policyLawsService.getAllPolicyLaws(langCode).subscribe((res: any) => {
      const apiData: any[] = res.data || [];

      this.policyLaws = apiData
        .filter(item => item.translations && item.translations.length > 0)
        .flatMap(item =>
          item.translations.map((t: any) => ({
            policyLawId: item.policyLawId,
            image: item.image,
            order: item.order,
            isActive: item.isActive,
            languageCode: t.languageCode,
            languageId: t.languageId,
            title: t.title,
            summary: t.summary
          }))
        );

      this.filterPolicyLaws(this.activeTab || 'kh');
    });
  }

  setTab(tab: string) {
    this.activeTab = tab;
    this.currentPage = 1;
    this.filterPolicyLaws(tab);
  }

  setTabPopup(tab: number) {
    this.activeTabPopup = tab;
  }

  filterPolicyLaws(tab: string) {
    this.filteredPolicyLaws = this.policyLaws.filter(r => r.languageCode === tab);
  }

  getLanguageCount(lang: string): number {
    return this.policyLaws.filter(r => r.languageCode === lang).length;
  }

  get pagedPolicyLaws(): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.filteredPolicyLaws.slice(start, end);
  }

  changePage(page: number) {
    this.currentPage = page;
  }

  totalPages(): number {
    return Math.ceil(this.filteredPolicyLaws.length / this.pageSize);
  }

  visibleButtonControl(eventName: string) {
    this.isEnableSave = false;
    this.isEnableUpdate = false;
    this.isEnableDelete = false;

    if (eventName === 'Add') { this.isEnableSave = true; }
    else if (eventName === 'Edit') { this.isEnableUpdate = true; }
    else if (eventName === 'Delete') { this.isEnableDelete = true; }
  }

  openModal(eventName?: string) {
    if (eventName) {
      this.visibleButtonControl(eventName);
    }
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
  }

  viewData(id: number, eventName: string) {
    this.policyLawId = id;

    this.policyLawsService.getPolicyLawById(id, '').subscribe({
      next: (res: any) => {
        if (res.status === 'success') {
          const data = res.data;

          this.policyLawId = data.policyLawId;
          this.order = data.order;
          this.isActive = data.isActive;
          this.image = null;
          this.imagePreview = data.image || '';

          data.translations.forEach((item: any) => {
            if (item.languageCode === 'kh') {
              this.titleKh = item.title;
              this.summaryKh = item.summary;
            } else if (item.languageCode === 'en') {
              this.titleEn = item.title;
              this.summaryEn = item.summary;
            }
          });

          this.openModal(eventName);
        } else {
          this.errorMessage = res.message;
        }

        setTimeout(() => {
          this.clearMsg();
          this.cdr.detectChanges();
        }, 3000);
      },
      error: (err) => {
        this.errorMessage = 'Failed to load policy law data';
        setTimeout(() => {
          this.clearMsg();
          this.cdr.detectChanges();
        }, 3000);
      }
    });
  }

  onFileSelected(event: any) {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      this.image = file;
      this.imagePreview = URL.createObjectURL(file);
    }
  }

  getRequest(policyLawId?: number): PolicyLawsRequest {
    return {
      image: this.image instanceof File ? this.image : undefined,
      data: {
        policyLawId: policyLawId ?? 0,
        order: this.order,
        isActive: this.isActive,
        translations: [
          {
            languageId: 1,
            title: this.titleKh ?? '',
            summary: this.summaryKh ?? ''
          },
          {
            languageId: 2,
            title: this.titleEn ?? '',
            summary: this.summaryEn ?? ''
          }
        ]
      }
    };
  }

  resetForm() {
    this.policyLawId = 0;
    this.order = 0;
    this.isActive = true;
    this.image = null;
    this.imagePreview = '';
    this.titleKh = '';
    this.summaryKh = '';
    this.titleEn = '';
    this.summaryEn = '';
  }

  refreshData(status: string, message: string) {
    if (status === 'success') {
      this.successMessage = message;
      this.closeModal();
      this.loadPolicyLaws('');
      this.resetForm();
    } else {
      this.errorMessage = message;
    }

    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }

  clearMsg() {
    this.successMessage = '';
    this.errorMessage = '';
  }

  exceptionData(message: string) {
    this.errorMessage = message;
    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }

  addNew() {
    this.resetForm();
    this.openModal('Add');
  }

  save() {
    const request = this.getRequest(0);
    this.clearMsg();

    this.policyLawsService.create(request).subscribe({
      next: (res) => {
        this.refreshData(res.status, res.message);
      },
      error: (err) => {
        this.exceptionData(err);
      }
    });
  }

  update() {
    this.clearMsg();

    const request = this.getRequest(this.policyLawId);
    this.policyLawsService.update(this.policyLawId, request).subscribe({
      next: (res) => {
        this.refreshData(res.status, res.message);
      },
      error: (err) => {
        this.exceptionData(err);
      }
    });
  }

  delete() {
    if (!confirm('Are you sure you want to delete ?')) {
      return;
    }

    this.policyLawsService.delete(this.policyLawId).subscribe({
      next: (res) => {
        this.refreshData(res.status, res.message);
      },
      error: (err) => {
        this.exceptionData(err);
      }
    });
  }
}
